import React, { useState, useContext, useEffect, useRef, useMemo, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { createProtectedAccount } from "../../service/Security";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import { Search, X } from "lucide-react";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import moment from "moment";
import {
  getSkippedAccounts,
  getCreatedCount,
  downloadSkippedAccountsExcel,
} from "../../utils/skippedAccountsExport";
import AttachProofInput from "../common/AttachProofInput";
import { uploadFileToS3 } from "../../service/S3Bucket";
import { PROTECTION_JOBS_ROUTE } from "./ProtectionJobs";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const MAX_PROTECTED_ACCOUNTS = 250;

export const CreateAccountModal = ({ open, onClose, onSaved }) => {
  const lastItemRef = useRef(null);
  const [accountNo, setAccountNo] = useState("");
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState("");
  const [isPermanentProtection, setIsPermanentProtection] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [forInstalmentPlan, setForInstalmentPlan] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [proofFile, setProofFile] = useState(null); // newly picked file, not yet uploaded
  const [existingAttachment, setExistingAttachment] = useState(""); // url already on the row
  const { particularAccount, isEditRequest, projectId, theme, langMode } =
    useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";
  const navigate = useNavigate();

  useEffect(() => {
    if (isEditRequest && particularAccount) {
      setAccountNo(particularAccount.accountNo || "");
      setIsPermanentProtection(particularAccount.permanent || false);
      setFromDate(particularAccount.fromDate || "");
      setToDate(particularAccount.toDate || "");
      setRemarks(particularAccount.remarks || "");
      setForInstalmentPlan(particularAccount.forInstalmentPlan || false);
      setExistingAttachment(particularAccount.attachment || "");
      setProofFile(null);
    }
  }, [isEditRequest, particularAccount]);

  // Modal instances stay mounted between opens (unlike the old routed page,
  // which remounted fresh on every navigation) -- reset local fields on close
  // so the next "create" open doesn't show a stale previous entry.
  useEffect(() => {
    if (!open) {
      setAccountNo("");
      setFromDate(moment().format("YYYY-MM-DD"));
      setToDate("");
      setIsPermanentProtection(false);
      setRemarks("");
      setForInstalmentPlan(false);
      setAccountNumbers([]);
      setProofFile(null);
      setExistingAttachment("");
    }
  }, [open]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  // One combined message: mentions the protected accounts too when the save was
  // partial, so the success toast never has to fire alongside the warning.
  const buildSkippedWarning = (skippedCount, createdCount) => {
    if (skippedCount <= 0) return "";
    if (createdCount > 0) {
      return translations[langMode].partialProtectionSummary
        .replace("{created}", createdCount)
        .replace("{skipped}", skippedCount);
    }
    return `${skippedCount} ${translations[langMode].skippedAccountsDownloaded}`;
  };

  const addProtectedaccount = async () => {
    if (!isEditRequest && accountNumbers.length > MAX_PROTECTED_ACCOUNTS) {
      toast.error(
        translations[langMode].protectedAccountLimitReached.replace(
          "{limit}",
          MAX_PROTECTED_ACCOUNTS
        )
      );
      return;
    }
    if (!remarks) {
      toast.error(translations[langMode].addremark);
      return;
    }
    if (!isPermanentProtection && (!fromDate || !toDate)) {
      toast.error(translations[langMode].pleaseSelectFromDateToDate);
      return;
    }
    const token = Cookies.get("accessToken");

    // The API takes the attachment as an already-uploaded url string, so a newly
    // picked file goes to S3 first. With no new file we send back whatever the
    // row already had -- the backend overwrites the column with what it is given,
    // so omitting it on an edit would silently clear the existing document.
    setIsLoading(true);
    let attachment = existingAttachment || null;
    if (proofFile) {
      try {
        attachment = await uploadFileToS3(proofFile);
      } catch (error) {
        setIsLoading(false);
        console.error("Attachment upload failed:", error);
        toast.error(translations[langMode].failedtouploadfiles);
        return;
      }
    }

    const proctedAcc = {
      id: particularAccount.id ? particularAccount.id : null,
      ...(isEditRequest ? { accountNo: accountNo } : { accountNos: accountNumbers }),
      permanent: isPermanentProtection,
      fromDate: isPermanentProtection ? null : formatDate(fromDate),
      toDate: isPermanentProtection ? null : formatDate(toDate),
      editRequest: isEditRequest ? isEditRequest : false,
      projectId: projectId,
      remarks: remarks,
      forInstalmentPlan: forInstalmentPlan,
      attachment: attachment,
    };

    try {
      const data = await createProtectedAccount(proctedAcc, token);
      setIsLoading(false)

      // The request now runs as a background job (QUEUED + jobId). Progress and the skipped
      // accounts are shown on the Protection Jobs screen, so take the user there.
      if (data?.header?.code === 600 && data?.body?.value?.jobId != null) {
        // create-or-update answers with its own text in body.value.message.
        toast.success(data.body.value.message || translations[langMode].protectionRequestReceived);
        onSaved && onSaved();
        onClose();
        navigate(PROTECTION_JOBS_ROUTE);
        return;
      }

      // Accounts the backend refused (e.g. "Already permanently protected") come
      // back as `skippedAccounts` -- export them straight to Excel so the user
      // has the full list instead of a truncated toast.
      const skippedAccounts = getSkippedAccounts(data);
      const skippedCount = skippedAccounts.length;
      if (skippedCount > 0) {
        downloadSkippedAccountsExcel(skippedAccounts);
      }
      // Whenever anything was skipped the warning replaces the success toast, so
      // the user only ever sees a single toast per save.
      const skippedWarning = buildSkippedWarning(
        skippedCount,
        getCreatedCount(
          data,
          Math.max((isEditRequest ? 1 : accountNumbers.length) - skippedCount, 0)
        )
      );

      if (data.header.code === 600) {
        if (skippedWarning) {
          toast.warning(skippedWarning);
        } else {
          toast.success(
            isEditRequest
              ? translations[langMode].protectedAccUpdated
              : translations[langMode].protectedAcccreated
          );
        }
        if (!isEditRequest) {
          setAccountNo("");
          setFromDate("");
          setToDate("");
          setIsPermanentProtection(false);
          setRemarks(""); // Reset remarks
          setForInstalmentPlan(false);
          setProofFile(null);
          setExistingAttachment("");
        }
        onSaved && onSaved();
        onClose();
      } else if (skippedWarning) {
        toast.warning(skippedWarning);
      } else if (typeof data?.body?.value === "string") {
        toast.error(data.body.value);
      } else {
        toast.error(translations[langMode].errorcreatingprotacc);
      }
    } catch (error) {
      setIsLoading(false)
      // The skipped list can also come back on a non-2xx response.
      const skippedAccounts = getSkippedAccounts(error?.response?.data);
      if (skippedAccounts.length > 0) {
        downloadSkippedAccountsExcel(skippedAccounts);
        toast.warning(
          buildSkippedWarning(
            skippedAccounts.length,
            getCreatedCount(error?.response?.data, 0)
          )
        );
        return;
      }
      toast.error(translations[langMode].errorcreatingprotacc);
    }
  };

  const getTomorrowDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Add 1 day
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const minDate = getTomorrowDate();
  const minDateObj = useMemo(() => parseYmd(minDate), [minDate]);
  const toDateMin = useMemo(() => {
    const f = parseYmd(fromDate);
    if (!f || !minDateObj) return minDateObj;
    return f > minDateObj ? f : minDateObj;
  }, [fromDate, minDateObj]);

  const handleAccountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setAccountNo(value);
    }
  };

  const commitAccountNumber = () => {
    if (accountNo?.trim()) {
      if (accountNumbers.length >= MAX_PROTECTED_ACCOUNTS) {
        toast.error(
          translations[langMode].protectedAccountLimitReached.replace(
            "{limit}",
            MAX_PROTECTED_ACCOUNTS
          )
        );
        return;
      }
      setAccountNumbers((prev) => [...prev, accountNo.trim()]);
      setAccountNo("");
    }
  };

  const handleAccountKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitAccountNumber();
    }
  };

  const handlePasteExcel = (e) => {
    e.preventDefault();
    let values = e.clipboardData
      .getData("text")
      .split(/\r?\n|\t/)
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const availableSlots = Math.max(MAX_PROTECTED_ACCOUNTS - accountNumbers.length, 0);
    if (values.length > availableSlots) {
      toast.error(
        translations[langMode].protectedAccountLimitReached.replace(
          "{limit}",
          MAX_PROTECTED_ACCOUNTS
        )
      );
    }
    setAccountNumbers((prev) => [
      ...prev,
      ...values.slice(0, Math.max(MAX_PROTECTED_ACCOUNTS - prev.length, 0)),
    ]);
  };

  const removeAccountNumber = (index) => {
    setAccountNumbers((prev) => prev.filter((_, i) => i !== index));
  };

  const searchAccountNO = (e) => {
    e.preventDefault();
    commitAccountNumber();
  }
  useEffect(() => {
    if (lastItemRef.current) {
      lastItemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [accountNumbers]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 h-full w-screen overflow-hidden">
          <div className="flex h-full items-center justify-center overflow-hidden p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                dir={isRtl ? "rtl" : "ltr"}
                className={`relative my-auto flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-full flex-col transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all sm:w-full sm:max-w-2xl ${isRtl ? "text-right" : "text-left"} ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
              >
                <div className={`flex min-h-0 flex-1 flex-col bg-white ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
                  <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isEditRequest
                        ? translations[langMode].editacc
                        : translations[langMode].createacc}
                    </h3>
                    <ModalCloseButton onClick={onClose} ariaLabel="Close modal" />
                  </div>
                  <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-5 sm:py-6">
                    <div className="w-full">
                      <div className="relative">
                        <div className="min-h-[40px] flex justify-between items-center">
                          <div className="relative min-w-0 w-full">
                            <div className={`overflow-hidden rounded-md border border-gray-400 py-0.5 pr-1 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400 ${theme === "dark"
                              ? "dark-mode"
                              : theme === "high-contrast"
                                ? "high-contrast"
                                : "bg-white"
                              }`}>
                            {/* Scroll on an inner box so the scrollbar stays inside the rounded border. */}
                            <div className="flex min-h-[34px] max-h-[114px] min-w-0 flex-wrap items-center gap-1 overflow-y-auto overscroll-contain px-3 pb-1 pt-[14px]">
                              {!isEditRequest && accountNumbers?.map((number, index) => (
                                <div
                                  key={index}
                                  ref={index === accountNumbers.length - 1 ? lastItemRef : null}
                                  className="max-w-full shrink-0 bg-gray-100 px-2 py-0.5 text-sm text-gray-700 rounded-md flex min-w-0 items-center gap-1 border border-gray-200"
                                >
                                  <span className="min-w-0 truncate">{number}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeAccountNumber(index)}
                                    aria-label={translations[langMode]?.removeAccountNumber ?? "Remove account number"}
                                    className="text-gray-400 hover:text-gray-600 ml-0.5 inline-flex shrink-0 items-center justify-center"
                                  >
                                    <X size={13} />
                                  </button>
                                </div>
                              ))}
                              <input
                                id="account-input"
                                type="text"
                                value={accountNo}
                                onChange={handleAccountChange}
                                onKeyDown={isEditRequest ? undefined : handleAccountKeyPress}
                                onPaste={isEditRequest ? undefined : handlePasteExcel}
                                onBlur={isEditRequest ? undefined : commitAccountNumber}
                                className={`min-w-[120px] flex-1 border-0 bg-transparent px-0 py-1 text-sm text-gray-900 outline-none focus:ring-0 ${theme === "dark"
                                  ? "dark-mode"
                                  : theme === "high-contrast"
                                    ? "high-contrast"
                                    : ""
                                  }`}
                              />
                            </div>
                          </div>
                            <label
                              htmlFor="account-input"
                              className={`absolute left-3 top-0 z-10 -translate-y-1/2 bg-white px-1.5 text-sm text-gray-500 ${theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : ""
                                }`}
                            >
                              {translations[langMode].accno}{" "}
                              <span className="text-red-500">*</span>
                            </label>
                          </div>
                          <button
                            onClick={searchAccountNO}
                            className="lg:hidden inline-flex h-10 min-h-[40px] w-10 items-center justify-center rounded-[10px] bg-[#236DB4] text-white"
                          >
                            <Search size={18} />
                          </button>
                        </div>
                        <div className="mt-1 ml-2 flex flex-col gap-1 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                          <p className="min-w-0 sm:flex-1">{translations[langMode].pressEnter}</p>
                          {!isEditRequest && (
                            <p className="shrink-0 text-xs text-gray-500 sm:whitespace-nowrap">
                              {translations[langMode].protectedAccountCount
                                .replace("{count}", accountNumbers.length)
                                .replace("{limit}", MAX_PROTECTED_ACCOUNTS)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {translations[langMode].remark}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        className={`form-textarea required:border border-gray-300 dark:border-gray-600 rounded-md w-full h-24 mt-1 px-3 py-2 text-gray-700 dark:text-gray-300 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400
      ${!remarks ? "border-red-500" : ""}
      ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : ""
                          }`}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder={translations[langMode].enterremark}
                      />
                      {!remarks && (
                        <p className="text-red-500 text-sm mt-1">
                          {translations[langMode].RemarkReq}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <input
                        type="checkbox"
                        id="permanent-protection"
                        className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                        checked={isPermanentProtection}
                        onChange={(e) => setIsPermanentProtection(e.target.checked)}
                      />
                      <label htmlFor="permanent-protection" className="flex items-center cursor-pointer">
                        <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {translations[langMode].permanent}
                        </span>
                      </label>
                      {!isPermanentProtection && (
                        <div className={`flex justify-center xl:flex-nowrap lg:flex-nowrap md:flex-nowrap sm:flex-nowrap flex-wrap gap-y-3 ${langMode === "ar" ? "gap-3" : "gap-4"}`}>
                          <div className="w-21 md:w-36 ">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => {
                                setFromDate(date);
                                if (toDate && date && toDate < date) {
                                  setToDate(null);
                                }
                              }}
                              placeholderText={translations[langMode].fromdate}
                              minDate={minDate}
                              selectsStart
                              startDate={fromDate}
                              endDate={toDate}
                              className={`w-full h-9.5 px-2 !rounded-md text-sm border border-gray-400 form-input focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""
                                }`}
                              calendarClassName="!rounded-lg shadow-lg"
                              popperPlacement="bottom-start"
                              popperContainer={({ children }) => (
                                <div style={{ position: "fixed", zIndex: 100000 }}>
                                  {children}
                                </div>
                              )}
                            />
                          </div>
                          <div className="w-21 md:w-36 ">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              placeholderText={translations[langMode].todate}
                              minDate={fromDate || minDate}
                              selectsEnd
                              startDate={fromDate}
                              endDate={toDate}
                              disabled={!fromDate}
                              className={`w-full h-9.5 px-2 !rounded-md text-sm border border-gray-400 form-input focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""
                                }`}
                              calendarClassName="!rounded-lg shadow-lg"
                              popperPlacement="bottom-start"
                              popperContainer={({ children }) => (
                                <div style={{ position: "fixed", zIndex: 100000 }}>
                                  {children}
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <AttachProofInput
                      file={proofFile}
                      onChange={setProofFile}
                      theme={theme}
                      langMode={langMode}
                      existingUrl={existingAttachment}
                      onRemoveExisting={() => setExistingAttachment("")}
                    />
                  </div>
                  <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-600">
                    <CommonButton colorIntent="danger"
                      variant="danger"
                      onClick={onClose}
                    >
                      {translations[langMode].cancel}
                    </CommonButton>
                    <CommonButton colorIntent="success"
                      type="submit"
                      onClick={addProtectedaccount}
                      variant="primary"
                      disabled={isLoading}
                      className={isLoading ? "bg-opacity-60" : "bg-opacity-100"}
                    >
                      {isLoading ? translations[langMode].Processing :
                        isEditRequest
                          ? translations[langMode].update
                          : translations[langMode].save}
                    </CommonButton>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
